import { Dispatch, SetStateAction, useMemo } from "react";
import { ThreeDPQuiz } from "@WahooFitness/cloud-client-ts";
import {
  ImperialMetricDisplayPreference,
  ThreeDPQuizResultsType,
  UserType,
} from "@WahooFitness/cloud-client-types";
import { useCloudContext, useConfigContext, useOfflineSWR } from "@WahooFitness/wahoo-offline-mfe";
import { SourcePage } from "@/services/Analytics/EventTypes";
import useQuiz from "../useQuiz";
import useQuizOperations from "../useQuizOperations";
import ThreeDPPages from "./ThreeDPPages";

function useThreeDPQuiz(
  user: UserType,
  setResults: Dispatch<SetStateAction<ThreeDPQuizResultsType | undefined>>,
  setPage: Dispatch<SetStateAction<number>>,
  setError: Dispatch<SetStateAction<string | undefined>>,
  skipEducation: boolean,
  onFirstStepBack?: () => void
) {
  const { getCloudClient } = useCloudContext();
  const { wahooToken, locale } = useConfigContext();

  const unitSystem =
    user?.display_preference?.speed_distance === ImperialMetricDisplayPreference.imperial
      ? "imperial"
      : "metric";

  const quizClient = useMemo(() => getCloudClient(ThreeDPQuiz), [getCloudClient]);

  const {
    data: quizData,
    isLoading: quizLoading,
    error: quizError,
  } = useOfflineSWR(["threeDPquizQuestions", locale, unitSystem], () =>
    quizClient.get(wahooToken, locale, unitSystem)
  );

  const { getQuizResults, buildResponse } = useQuizOperations<ThreeDPQuizResultsType>(quizClient);

  return useQuiz(user, setResults, setPage, setError, skipEducation, {
    quizData,
    quizLoading,
    quizError,
    getQuizResults,
    buildResponse,
    pages: ThreeDPPages,
    analyticsEventName: "3dp_quiz",
    analyticsSourcePage: SourcePage.threedp_quiz,
    onFirstStepBack,
  });
}

export default useThreeDPQuiz;
